import {
  sanitizeOpenWorkTabTitle,
  UNNAMED_OPEN_WORK_TAB_TITLE,
  type OpenWorkTabTitle,
} from './open-work-tab-naming.ts';

export const MAX_OPEN_WORK_TABS = 5;
// Pins never take the last slot, so opening other work always has a tab to reuse.
export const MAX_PINNED_OPEN_WORK_TABS = MAX_OPEN_WORK_TABS - 1;

const MAX_STORED_TAB_CANDIDATES = 100;
const MAX_TAB_ID_CHARACTERS = 4_096;
const MAX_DESTINATION_CHARACTERS = 4_096;
const MAX_TAB_SECTION_CHARACTERS = 100;
const UNSAFE_DESTINATION_CHARACTERS = /[\u0000-\u001f\u007f]/;

// A tab's name is only as trustworthy as where it came from. A guess from the
// URL or the browser title, a name remembered from earlier in this browser
// session, a name the route loaded, and a name the page declared each give way
// only to an equal or more trustworthy one, so a known name never flashes back
// to a guess.
export type OpenWorkTabTitleSource = 'guessed' | 'remembered' | 'loaded' | 'declared';

const TITLE_SOURCE_RANK: Record<OpenWorkTabTitleSource, number> = {
  guessed: 0,
  remembered: 1,
  loaded: 2,
  declared: 3,
};

export function titleSourceReplaces(
  next: OpenWorkTabTitleSource,
  current: OpenWorkTabTitleSource,
): boolean {
  return TITLE_SOURCE_RANK[next] >= TITLE_SOURCE_RANK[current];
}

function titleSource(value: unknown): OpenWorkTabTitleSource {
  return typeof value === 'string' && Object.hasOwn(TITLE_SOURCE_RANK, value)
    ? (value as OpenWorkTabTitleSource)
    : 'guessed';
}

export interface OpenWorkTab {
  id: string;
  path: string;
  title: OpenWorkTabTitle;
  titleSource: OpenWorkTabTitleSource;
  section: string | null;
  lastUsed: number;
  /** The record path a pinned tab keeps; null for a tab its area reuses. */
  pinnedRoot: string | null;
}

export interface OpenWorkTabCandidate {
  id?: unknown;
  path?: unknown;
  title?: unknown;
  titleSource?: unknown;
  section?: unknown;
  lastUsed?: unknown;
  pathMatch?: unknown;
  pathPrefix?: unknown;
  pinnedRoot?: unknown;
}

function normalizedPathname(path: unknown): string {
  if (typeof path !== 'string' || path.length > MAX_DESTINATION_CHARACTERS) return '';
  const pathname = path.trim().split('?')[0] ?? '';
  const withoutTrailingSlash = pathname.replace(/\/+$/, '');
  return (withoutTrailingSlash || '/').toLowerCase();
}

function normalizedDestination(path: unknown): string {
  if (typeof path !== 'string' || path.length > MAX_DESTINATION_CHARACTERS) return '';
  const trimmed = path.trim();
  if (
    !trimmed ||
    !trimmed.startsWith('/') ||
    trimmed.startsWith('//') ||
    UNSAFE_DESTINATION_CHARACTERS.test(trimmed)
  )
    return '';
  const queryIndex = trimmed.indexOf('?');
  const pathname = queryIndex >= 0 ? trimmed.slice(0, queryIndex) : trimmed;
  const search = queryIndex >= 0 ? trimmed.slice(queryIndex + 1) : '';
  return `${normalizedPathname(pathname)}${search ? `?${search}` : ''}`;
}

function boundedSection(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return value.slice(0, MAX_TAB_SECTION_CHARACTERS).trim() || null;
}

export function pinnedRootOwnsPathname(root: string, pathname: string): boolean {
  return pathname === root || pathname.startsWith(`${root}/`);
}

export function pinnedWorkTabId(root: string): string {
  return `pinned:${root}`;
}

function pinnedRoot(value: unknown, path: string): string | null {
  if (
    typeof value !== 'string' ||
    !value.startsWith('/') ||
    value.length > MAX_DESTINATION_CHARACTERS
  )
    return null;
  const pathname = path.trim().split(/[?#]/, 1)[0] ?? '';
  return pinnedRootOwnsPathname(value, pathname) ? value : null;
}

export function pageWorkTabId(pathname: unknown): string | null {
  const normalizedPath = normalizedPathname(pathname);
  return normalizedPath ? `page:${normalizedPath}` : null;
}

export function findOpenWorkTabByDestination(items: unknown, path: unknown): OpenWorkTab | null {
  const destination = normalizedDestination(path);
  if (!destination) return null;
  return (
    sanitizeOpenWorkTabs(items).find((tab) => normalizedDestination(tab.path) === destination) ||
    null
  );
}

function declaredWorkTabMatchesPath(
  pathname: unknown,
  { pathMatch, pathPrefix }: OpenWorkTabCandidate = {},
): boolean {
  if (!pathPrefix) return true;
  const normalizedPath = normalizedPathname(pathname);
  const normalizedPrefix = normalizedPathname(pathPrefix);
  if (!normalizedPath || !normalizedPrefix) return false;
  if (pathMatch === 'exact') return normalizedPath === normalizedPrefix;
  return normalizedPath === normalizedPrefix || normalizedPath.startsWith(`${normalizedPrefix}/`);
}

export function resolveDeclaredWorkTab<Candidate extends OpenWorkTabCandidate>(
  pathname: unknown,
  workTab: Candidate | null | undefined,
): Candidate | null | undefined {
  if (!workTab?.id) return undefined;
  return declaredWorkTabMatchesPath(pathname, workTab) ? workTab : null;
}

export function sanitizeOpenWorkTabs(items: unknown): OpenWorkTab[] {
  if (!Array.isArray(items)) return [];

  const seenIds = new Set<string>();
  const seenDestinations = new Set<string>();
  const tabs: OpenWorkTab[] = [];

  for (const item of items.slice(0, MAX_STORED_TAB_CANDIDATES)) {
    if (typeof item !== 'object') continue;
    const candidate = item as OpenWorkTabCandidate;
    if (
      !candidate ||
      typeof candidate.path !== 'string' ||
      candidate.path.length > MAX_DESTINATION_CHARACTERS ||
      !candidate.path.trim()
    )
      continue;
    const path = candidate.path.trim();
    const root = pinnedRoot(candidate.pinnedRoot, path);
    const id = root
      ? pinnedWorkTabId(root)
      : typeof candidate.id === 'string' &&
          candidate.id.length <= MAX_TAB_ID_CHARACTERS &&
          candidate.id.trim()
        ? candidate.id.trim()
        : pageWorkTabId(path);
    const destination = normalizedDestination(path);
    if (!id || !destination) continue;
    if (seenIds.has(id) || seenDestinations.has(destination)) continue;

    seenIds.add(id);
    seenDestinations.add(destination);
    const title = sanitizeOpenWorkTabTitle(candidate.title);
    tabs.push({
      id,
      path,
      title: title ?? UNNAMED_OPEN_WORK_TAB_TITLE,
      titleSource: title ? titleSource(candidate.titleSource) : 'guessed',
      section: boundedSection(candidate.section),
      lastUsed:
        typeof candidate.lastUsed === 'number' &&
        Number.isSafeInteger(candidate.lastUsed) &&
        candidate.lastUsed >= 0
          ? candidate.lastUsed
          : 0,
      pinnedRoot: root,
    });
  }

  return tabs.slice(0, MAX_OPEN_WORK_TABS);
}

export function visitOpenWorkTab(
  items: unknown,
  candidate: OpenWorkTabCandidate | null | undefined,
  visitedAt = Date.now(),
): OpenWorkTab[] {
  const tabs = sanitizeOpenWorkTabs(items);
  if (typeof candidate?.id !== 'string' || !candidate.id.trim()) return tabs;
  const visitedTab = sanitizeOpenWorkTabs([{ ...candidate, lastUsed: visitedAt }])[0];
  if (!visitedTab) return tabs;

  const destination = normalizedDestination(visitedTab.path);
  const matchingIndexes = tabs.reduce<number[]>((indexes, tab, index) => {
    const hasSameIdentity = tab.id === visitedTab.id;
    // A pinned tab is replaced only by its own record, never absorbed by its area.
    const hasSameDestination = !tab.pinnedRoot && normalizedDestination(tab.path) === destination;
    const belongsToDeclaredContext =
      !tab.pinnedRoot &&
      Boolean(candidate.pathPrefix) &&
      declaredWorkTabMatchesPath(tab.path, candidate);
    if (hasSameIdentity || hasSameDestination || belongsToDeclaredContext) indexes.push(index);
    return indexes;
  }, []);

  if (matchingIndexes.length > 0) {
    const matchingIndexSet = new Set(matchingIndexes);
    const insertionIndex = matchingIndexes[0]!;
    const remainingTabs = tabs.filter((_, index) => !matchingIndexSet.has(index));
    remainingTabs.splice(insertionIndex, 0, visitedTab);
    return remainingTabs;
  }

  if (tabs.length < MAX_OPEN_WORK_TABS) return [...tabs, visitedTab];

  const leastRecentlyUsedIndex = tabs.reduce<number>(
    (oldestIndex, tab, index) =>
      !tab.pinnedRoot && (oldestIndex < 0 || tab.lastUsed < tabs[oldestIndex]!.lastUsed)
        ? index
        : oldestIndex,
    -1,
  );
  return tabs.map((tab, index) => (index === leastRecentlyUsedIndex ? visitedTab : tab));
}

export function countPinnedOpenWorkTabs(items: readonly OpenWorkTab[]): number {
  return items.filter((tab) => tab.pinnedRoot).length;
}

// Pinning keeps the tab where it is and leaves its page as it was; only the
// tab's identity changes, so its area opens other records in a tab of their own.
export function pinOpenWorkTab(items: unknown, tabId: string, root: string): OpenWorkTab[] {
  const tabs = sanitizeOpenWorkTabs(items);
  const tab = tabs.find((candidate) => candidate.id === tabId);
  if (
    !tab ||
    tab.pinnedRoot ||
    tabs.some((candidate) => candidate.pinnedRoot === root) ||
    countPinnedOpenWorkTabs(tabs) >= MAX_PINNED_OPEN_WORK_TABS
  )
    return tabs;
  return sanitizeOpenWorkTabs(
    tabs.map((candidate) => (candidate === tab ? { ...candidate, pinnedRoot: root } : candidate)),
  );
}

// Unpinning returns the record to its area, which keeps one tab: the area's
// own tab when that is in front, otherwise the record just unpinned, in place.
export function unpinOpenWorkTab(
  items: unknown,
  tabId: string,
  areaTabId: string,
  activeTabId: string | null,
): OpenWorkTab[] {
  const tabs = sanitizeOpenWorkTabs(items);
  const pinned = tabs.find((candidate) => candidate.id === tabId && candidate.pinnedRoot);
  if (!pinned) return tabs;
  const areaTab = tabs.find((candidate) => candidate.id === areaTabId && !candidate.pinnedRoot);
  if (areaTab && areaTab.id === activeTabId) return tabs.filter((tab) => tab !== pinned);
  return sanitizeOpenWorkTabs(
    tabs.flatMap((tab) =>
      tab === areaTab ? [] : tab === pinned ? [{ ...tab, id: areaTabId, pinnedRoot: null }] : [tab],
    ),
  );
}

// A record pinned from a menu opens beside the tab whose menu offered it,
// without leaving the page in front.
export function pinOpenWorkTabDestination(
  items: unknown,
  besideTabId: string,
  destination: OpenWorkTab,
  root: string,
): OpenWorkTab[] {
  const tabs = sanitizeOpenWorkTabs(items);
  if (
    tabs.length >= MAX_OPEN_WORK_TABS ||
    tabs.some((candidate) => candidate.pinnedRoot === root) ||
    countPinnedOpenWorkTabs(tabs) >= MAX_PINNED_OPEN_WORK_TABS
  )
    return tabs;
  const besideIndex = tabs.findIndex((candidate) => candidate.id === besideTabId);
  const next = [...tabs];
  next.splice(besideIndex < 0 ? next.length : besideIndex + 1, 0, {
    ...destination,
    pinnedRoot: root,
  });
  return sanitizeOpenWorkTabs(next);
}

export function closeOpenWorkTab(items: unknown, tabId: string): OpenWorkTab[] {
  return sanitizeOpenWorkTabs(items).filter((tab) => tab.id !== tabId);
}

export function reorderOpenWorkTab(
  items: unknown,
  movingTabId: string,
  targetTabId: string,
): OpenWorkTab[] {
  const tabs = sanitizeOpenWorkTabs(items);
  const movingIndex = tabs.findIndex((tab) => tab.id === movingTabId);
  const targetIndex = tabs.findIndex((tab) => tab.id === targetTabId);
  if (movingIndex < 0 || targetIndex < 0 || movingIndex === targetIndex) return tabs;

  const reordered = [...tabs];
  const [movingTab] = reordered.splice(movingIndex, 1);
  reordered.splice(targetIndex, 0, movingTab!);
  return reordered;
}
